/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, SyntheticEvent, useEffect, useState
} from 'react';
import {
  IlButton, IlInlineLoading, IlLoading, IlTextInput, IlTooltipTippy, IlInlineNotification
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CopyToClipboard from 'react-copy-to-clipboard';
import classnames from 'classnames';
import { noop } from 'lodash';
import {
  I18nNamespaces,
  LABEL_MAX_LENGTH,
  FORBIDDEN_CHARACTERS_REGEX,
  ValidationErrorKey as ValErr
} from '../../../../app/shared/utils/constants.util';
import { RootState } from '../../../../app/store';
import QrCode from '../../../../app/shared/components/QrCode/QrCode';
import CopyIcon from '../../../../app/shared/components/CopyIcon/CopyIcon';
import { showToast } from '../../../../app/shared/AppSlice/AppSlice';
import {
  enrollAuthenticator,
  setError,
  setValidationError,
  verifyOrActivateFactor,
  resetEnrollmentState
} from '../../setupSecuritySlice';
import { SecuritySetupType, AuthErrorKey } from '../../../../domain/models/impl/SecuritySetup';
import { getLoaValidationMessageKey, useRequiredLevel } from '../../../../app/shared/utils/helpers.util';
import usePreserveSearchNav from '../../../../app/shared/hooks/usePreserveSearchNav';

import styles from './EnrollAuthenticator.scss';

let timeout: ReturnType<typeof setTimeout>;

const EnrollAuthenticator = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const requiredLevel = useRequiredLevel();
  const { replaceLoaLevel } = usePreserveSearchNav();
  const actionLoading = useSelector((state: RootState) => state.setupSecurity.actionLoading);
  const selectedFactor = useSelector((s: RootState) => s.setupSecurity.selectedFactor);
  const validationError = useSelector((state: RootState) => state.setupSecurity.validationError);
  const qrCode = useSelector((state: RootState) => state.setupSecurity.qrCode);
  const secretKey = useSelector((state: RootState) => state.setupSecurity.secretKey);
  const isQrCodeLoading = useSelector((state: RootState) => state.setupSecurity.isQrCodeLoading);
  const authErrorKey = useSelector((state: RootState) => state.setupSecurity.error);
  const [code, setCode] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const factorId = selectedFactor?.factorId ?? '';
  const type = selectedFactor?.type ?? SecuritySetupType.AUTHENTICATOR;
  const digits = selectedFactor?.digits ?? 0;
  const level = selectedFactor?.loaLevel;

  const qrHasData = Boolean(qrCode);
  const secretHasData = Boolean(secretKey) && digits > 0;
  const canShowSecret = (qrHasData || secretHasData) && !isQrCodeLoading;
  const canVerify = Boolean(factorId) && digits > 0;
  const typedDigits = code.replace(/\D/g, '').length;
  const shouldShowInlineNotification = !canVerify && !!authErrorKey;
  const [label, setLabel] = useState('');
  const isLabelError = validationError?.startsWith('label');
  const isOtpError = !!validationError && !isLabelError;

  const canSubmit = canVerify
    && !actionLoading
    && typedDigits === digits
    && !validationError;

  const inlineTitleMap: Record<string, string> = {
    [AuthErrorKey.ResourceAlreadyExists]: t('loaEnrichment.common.errors.alreadyExistsInline', { ns: I18nNamespaces.LOA_ENRICHMENT }),
    [AuthErrorKey.ResourceForbidden]: t('loaEnrichment.common.errors.resourceForbiddenInline', { ns: I18nNamespaces.LOA_ENRICHMENT }),
    [AuthErrorKey.DefaultErrorMessage]: t(
      'loaEnrichment.factors.authenticator.inline.setupFailed',
      { ns: I18nNamespaces.LOA_ENRICHMENT }
    )
  };

  const inlineTitle = inlineTitleMap[authErrorKey]
    ?? t('loaEnrichment.factors.authenticator.inline.setupFailed', { ns: I18nNamespaces.LOA_ENRICHMENT });

  useEffect(() => {
    const data = { type: SecuritySetupType.AUTHENTICATOR };
    dispatch(enrollAuthenticator(data, t) as any);

    return () => {
      dispatch(resetEnrollmentState());
    };
  }, [dispatch, t]);

  useEffect(() => {
    if (!selectedFactor?.loaLevel) return;
    if (selectedFactor.loaLevel !== requiredLevel) {
      dispatch(showToast({
        title: t('loaEnrichment.levelAdjustedEnroll', { level: selectedFactor.loaLevel }),
        timeout: 0,
        kind: 'info',
        subtitle: ''
      }));
      replaceLoaLevel(selectedFactor.loaLevel);
    }
  }, [dispatch, selectedFactor?.loaLevel, requiredLevel, t, replaceLoaLevel]);

  const onImageLoad = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    setIsImageLoading(false);
    event.preventDefault();
  };

  const tooltipClickHandler = (): void => {
    setIsTooltipVisible(true);
    clearTimeout(timeout);
    timeout = global.setTimeout(() => setIsTooltipVisible(false), 3000);
  };

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setError(''));
    if (validationError && !validationError.startsWith('label')) {
      dispatch(setValidationError(''));
    }
    const input = e.currentTarget.value.replace(/\D/g, '');
    const clippedCode = input.slice(0, digits);
    setCode(clippedCode);
  };

  const updateLabel = (e: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(setError(''));

    if (validationError?.startsWith('label')) {
      dispatch(setValidationError(''));
    }

    const authLabel = e.currentTarget.value;

    if (authLabel.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
    }

    setLabel(e.currentTarget.value);
  };

  const submit = (ev: FormEvent | ChangeEvent): void => {
    ev.preventDefault();

    dispatch(setError(''));
    dispatch(setValidationError(''));

    if (!factorId) return;

    const normalized = label.trim();

    if (!normalized) {
      dispatch(setValidationError(ValErr.LabelRequired));
      return;
    }

    if (normalized.length > LABEL_MAX_LENGTH) {
      dispatch(setValidationError(ValErr.LabelTooLong));
      return;
    }

    if (FORBIDDEN_CHARACTERS_REGEX.test(label)) {
      dispatch(setValidationError(ValErr.LabelForbiddenChars));
      return;
    }

    const otp = code.replace(/\s/g, '');
    dispatch(verifyOrActivateFactor(t, {
      factorId,
      code: otp,
      label: normalized,
      action: 'activate',
      successToastMsg: 'loaEnrichment.common.messages.activated',
      elevateLevel: level ?? requiredLevel
    }));
  };

  return (
    <>
      {type === SecuritySetupType.AUTHENTICATOR
        && (
          <>
            <h1 id='testFactorTitle' className={classnames(styles.formTitle, styles.noPadding)}>
              {t('loaEnrichment.factors.authenticator.titlePreferredAuth')}
            </h1>
            <p id='testFactorHelperText' className={styles.helpText}>
              {t('loaEnrichment.factors.authenticator.authenticatorHelp')}
            </p>
            <div className={styles.helpTextOptions}>
              <span className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.microsoft')}</span>
              <span className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.google')}</span>
              <span className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.oneLogin')}</span>
              <span className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.authy')}</span>
              <span className={styles.authOption}>{t('loaEnrichment.factors.authenticator.apps.lastPass')}</span>
            </div>
          </>
        )}
      <div className={styles.qrContainer}>
        {
          isImageLoading ? <IlLoading className={styles.loader} withOverlay={false} /> : null
        }
        <QrCode onLoad={onImageLoad} hide={isImageLoading} source={qrCode} alt='Qr code' />
      </div>
      {shouldShowInlineNotification && (
        <IlInlineNotification
          title={inlineTitle}
          notificationType='inline'
          kind='error'
          hideCloseButton
        />
      )}
      {canShowSecret && (
        <div className={styles.container}>
          <p className={classnames(styles.text, styles.authSecureKeyLabel)}>
            {t('loaEnrichment.common.labels.secretCode')}
          </p>
          <p id='secret-key' className={classnames(styles.text, styles.leftMargin)}>
            {secretKey}
          </p>
          <div className={styles.leftMargin}>
            <CopyToClipboard text={secretKey}>
              <div>
                <IlTooltipTippy visible={isTooltipVisible} content={t('loaEnrichment.common.status.copied')} trigger='click'>
                  <span className={styles.blockSpan}>
                    <IlButton onClick={tooltipClickHandler} kind='ghost' className={classnames(styles.copyButton)}>
                      <CopyIcon />
                    </IlButton>
                  </span>
                </IlTooltipTippy>
              </div>
            </CopyToClipboard>
          </div>
        </div>
      )}
      {
        canVerify && (
          <>
            <div className={styles.break} />
            <form onSubmit={submit} className={styles.authForm}>
              <div className={styles.authLabel}>
                <IlTextInput
                  id='authenticator-label'
                  labelText={t('loaEnrichment.factors.authenticator.fields.label')}
                  placeholder={t('loaEnrichment.common.placeholders.label')}
                  type='text'
                  onChange={updateLabel}
                  invalid={!!isLabelError}
                  invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                  value={label}
                  autoFocus
                />
              </div>
              <p className={classnames(styles.helpText, styles.noPadding)}>
                {t('loaEnrichment.factors.authenticator.verifyEnrollHelpText', {
                  digits
                })}
              </p>
              <div>
                <IlTextInput
                  id='verification-code'
                  labelText={t('loaEnrichment.common.labels.verificationCode')}
                  placeholder={t('loaEnrichment.common.placeholders.code', {
                    digits
                  })}
                  type='text'
                  maxLength={digits}
                  invalid={isOtpError}
                  invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
                  value={code}
                  onChange={updateCode}
                  autoComplete='off'
                />
              </div>

              <div className={styles.activationContainer}>
                <IlButton
                  id='testActiveButton'
                  role='button'
                  kind='primary'
                  type='submit'
                  disabled={!canSubmit}
                > {t('loaEnrichment.common.buttons.activate')}
                </IlButton>
              </div>
            </form>
          </>
        )
      }
      {actionLoading && !isQrCodeLoading && (
        <div className={styles.loadingContainer}>
          <IlInlineLoading description={t('loaEnrichment.common.status.pleaseWait')} success={false} onSuccess={noop} />
        </div>
      )}
    </>
  );
};

export default EnrollAuthenticator;
